/**
 * Per-step explainer content for the onboarding cards' AI help panel.
 *
 * Each card's Sparkles icon opens a slide-over that "streams" this text: what
 * the step does, and the concrete conditions for it to reach `done`. Keyed by
 * the same step keys as the registry (ONBOARDING_STEPS). Steps without an entry
 * fall back to the registry description + dependsOn (see buildStepHelp).
 */
import { getStepDefinition } from './steps'

export interface StepHelp {
  /** Plain-language explanation of what this card/step does. */
  whatItDoes: string
  /** The concrete conditions that move this step to "done". */
  readyWhen: string[]
}

const STEP_HELP: Record<string, StepHelp> = {
  stripe_connect: {
    whatItDoes:
      "Connects the client's own Stripe account to the platform via Stripe Connect OAuth, so booking payments settle directly into their Stripe. No API keys are ever shared — the client just authorizes the connection.",
    readyWhen: [
      'The client completed the Stripe Connect authorization',
      'Their connected account id is stored on the account',
    ],
  },
  stripe_platform_webhook: {
    whatItDoes:
      'Registers the single platform-level Stripe Connect webhook that receives payment events for every connected client. Created once and reused across all accounts.',
    readyWhen: [
      'The Connect webhook endpoint exists in the platform Stripe account',
      'Its signing secret is configured',
    ],
  },
  limo_anywhere: {
    whatItDoes:
      "Validates the client's Limo Anywhere dispatch credentials and fetches their vehicle types, so paid bookings can be pushed into their dispatch system automatically.",
    readyWhen: [
      'Company ID, username, and password authenticate successfully',
      'Vehicle types were fetched and mapped to the client’s vehicles',
    ],
  },
  ghl_discovery: {
    whatItDoes:
      "Connects to the client's GoHighLevel CRM and loads their pipelines and stages, so you can choose where booking opportunities should be created and moved.",
    readyWhen: [
      'The GHL API key and location id authenticate',
      'Pipelines and stages loaded into the configuration dropdowns',
    ],
  },
  ghl_config: {
    whatItDoes:
      'Saves the chosen GoHighLevel pipeline and stage IDs (Quoted and Booked) so opportunities are created on quote and advanced automatically once payment succeeds.',
    readyWhen: [
      'Pipeline, Quoted stage, and Booked stage are selected and saved',
    ],
  },
  zapier_link: {
    whatItDoes:
      "Confirms the shared Zapier webhook and the account_slug payload field, so completed bookings flow into the client's Zapier automations.",
    readyWhen: [
      'The Zapier webhook URL is saved',
      'A test payload was received on the Zapier side',
    ],
  },
  ga4_property: {
    whatItDoes:
      'Creates a GA4 property and web data stream for the client and captures the Measurement ID that wires analytics into their site.',
    readyWhen: [
      'A GA4 property and web stream exist',
      'The Measurement ID is stored on the account',
    ],
  },
  ads_conversions: {
    whatItDoes:
      "Fetches the client's account-level Google Ads conversion ID and creates the standard website conversion actions under their Ads account (linked beneath the platform MCC).",
    readyWhen: [
      'The client’s Ads account is linked under the platform MCC',
      'The AW conversion ID and the conversion actions are created',
    ],
  },
  conversion_labels: {
    whatItDoes:
      'Captures the per-event Google Ads conversion labels (gads_label_*) the viewer fires for each funnel step. Google publishes these labels a few minutes after the conversion actions are created — so this step may need a moment.',
    readyWhen: [
      'All five labels are captured (get_vehicle_quotes, select_vehicle, provide_contact_info, purchase, generate_lead)',
      'If it returns empty, wait a few minutes and Refresh — Google hasn’t published the tag snippets yet',
    ],
  },
  microsoft_uet: {
    whatItDoes:
      "Stores the client's Microsoft Advertising UET (Universal Event Tracking) Tag ID. The viewer already loads the UET base tag and pushes id_uet into the dataLayer from this setting — so pasting the numeric Tag ID here wires Microsoft Ads tracking.",
    readyWhen: [
      'The numeric UET Tag ID (from Microsoft Advertising > Tools > UET tag) is pasted and saved',
    ],
  },
  stape_domain: {
    whatItDoes:
      "Creates the client's Stape.io server-side tagging domain and CDN, returning the CNAME targets they must add to DNS (br and load.br).",
    readyWhen: [
      'The Stape custom domain and CDN exist',
      'The br / load.br CNAME targets are returned',
    ],
  },
  stape_loader: {
    whatItDoes:
      "Generates the obfuscated custom loader script path and token for the client's Stape container, used to load GTM first-party.",
    readyWhen: ['The loader script path and token are generated'],
  },
  stape_settings: {
    whatItDoes:
      'Writes the stape_* account settings (domain, script path, datalayer token) so the viewer loads GTM through the first-party Stape domain instead of googletagmanager.com.',
    readyWhen: ['stape_enabled is true and all stape_* values are saved'],
  },
  gtm_server_url: {
    whatItDoes:
      "Appends the client's br.{domain} URL to the shared server-side GTM container so server events route to their Stape endpoint.",
    readyWhen: ['The br.{domain} URL is present on the server GTM container'],
  },
  vercel_domain: {
    whatItDoes:
      'Adds book.{domain} to the viewer project on Vercel and returns the CNAME target the client must point their book subdomain at.',
    readyWhen: [
      'book.{domain} is added to the Vercel viewer project',
      'Its recommended CNAME target is known',
    ],
  },
  generate_dns_email: {
    whatItDoes:
      'Builds the combined DNS instruction email (the book / br / load.br records) for the client, ready to copy or send.',
    readyWhen: ['The 3-record email is generated (and optionally sent to the client)'],
  },
  verify_dns: {
    whatItDoes:
      'Resolves the three CNAME records and marks each green once the client’s DNS has propagated. DNS can take minutes to hours — click Refresh to re-check.',
    readyWhen: ['All three CNAMEs resolve to their expected targets'],
  },
  smoke_test: {
    whatItDoes:
      'Runs Playwright booking scenarios (point-to-point, round-trip, hourly, contact form, quote-only) against the live site — completing test-mode purchases and asserting the dataLayer events fire. Nothing is charged and no reservation reaches the client’s dispatch or CRM.',
    readyWhen: [
      'Every scenario passes (scenarios skipped for features the account doesn’t use are fine)',
      'The domain is live, Stripe Connect is done, and the GTM/Stape tagging is in place',
    ],
  },
  verify_ads_active: {
    whatItDoes:
      'Checks whether the Google Ads conversion actions are recording conversions yet (status ENABLED with conversions in the last 30 days). Recording can lag hours after the tag first fires.',
    readyWhen: [
      'The primary actions (purchase, get_vehicle_quotes) are recording conversions',
      'If not yet, run the smoke test or wait, then Refresh',
    ],
  },
}

/**
 * Resolve help content for a step, falling back to the registry definition for
 * any step without an authored entry.
 */
export function getStepHelp(stepKey: string): StepHelp {
  const authored = STEP_HELP[stepKey]
  if (authored) return authored

  const def = getStepDefinition(stepKey)
  const deps = def?.dependsOn ?? []
  return {
    whatItDoes: def?.description ?? 'This onboarding step.',
    readyWhen: deps.length
      ? [`These steps complete first: ${deps.join(', ')}`, 'This step finishes successfully']
      : ['This step finishes successfully'],
  }
}
